
import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

export class MediaGallery extends Component {
  #controller = new AbortController();

  viewer = null;
  viewerStage = null;
  viewerImage = null;
  viewerState = null;
  previousFocus = null;

  artworkImages = [];
  artworkIndex = 0;
  imageLoadToken = 0;

  touchMode = null;
  touchStartX = 0;
  touchStartY = 0;
  touchStartTime = 0;
  pinchDistance = 0;
  pinchScale = 1;

  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const signal = this.#controller.signal;
    const section = this.closest('.shopify-section, dialog');

    section?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#handleVariantUpdate,
      { signal }
    );

    this.refs.zoomDialogComponent?.addEventListener(
      ThemeEvents.zoomMediaSelected,
      this.#handleZoomMediaSelected,
      { signal }
    );

    this.addEventListener('click', this.handleGalleryClick, {
      capture: true,
      signal
    });

    document.addEventListener(
      'keydown',
      this.handleViewerKeyDown,
      { signal }
    );
  }

  disconnectedCallback() {
    this.closeArtworkViewer(true);
    this.#controller.abort();
    super.disconnectedCallback();
  }

  #handleVariantUpdate = (event) => {
    const source = event.detail?.data?.html;
    const replacement = source?.querySelector('media-gallery');

    if (!replacement) return;

    this.closeArtworkViewer(true);
    this.replaceWith(replacement);
  };

  #handleZoomMediaSelected = (event) => {
    this.slideshow?.select(
      event.detail.index,
      undefined,
      { animate: false }
    );
  };

  getArtworkImage(event) {
    const target = event.target;

    if (!(target instanceof Element)) return null;

    if (target.closest(
      'button, input, select, textarea, ' +
      '.slideshow-controls, .slideshow-navigation'
    )) {
      return null;
    }

    const image = target.closest('img.product-media__image');

    return image && this.contains(image) ? image : null;
  }

  getArtworkSource(image) {
    return (
      image.getAttribute('data_max_resolution') ||
      image.getAttribute('data-max-resolution') ||
      image.currentSrc ||
      image.src
    );
  }

  collectArtworkImages() {
    const media = Array.from(this.refs.media || []);

    const images = media
      .map(item =>
        item.querySelector('img.product-media__image')
      )
      .filter(Boolean);

    if (images.length) return [...new Set(images)];

    return Array.from(
      this.querySelectorAll('img.product-media__image')
    ).filter(image => !image.closest('zoom-dialog'));
  }

  createArtworkViewer() {
    if (this.viewer) return;

    const oldStyles = document.getElementById(
      'tm-artwork-viewer-styles'
    );

    oldStyles?.remove();

    const style = document.createElement('style');
    style.id = 'tm-artwork-viewer-styles';

    style.textContent = `
      .tm-artwork-viewer {
        position: fixed !important;
        inset: 0 !important;
        z-index: 2147483000 !important;
        display: flex !important;
        align-items: center;
        justify-content: center;
        background: rgba(12,12,12,.96);
        opacity: 0;
        visibility: hidden;
        transition: opacity .25s ease, visibility .25s ease;
        isolation: isolate;
      }

      .tm-artwork-viewer.is-open {
        opacity: 1 !important;
        visibility: visible !important;
      }

      .tm-artwork-stage {
        position: absolute !important;
        inset: 58px 58px 72px !important;
        overflow: hidden;
        touch-action: none !important;
        cursor: zoom-in;
        z-index: 1;
      }

      .tm-artwork-stage.is-zoomed {
        cursor: grab;
      }

      .tm-artwork-stage.is-dragging {
        cursor: grabbing;
      }

      .tm-artwork-image {
        position: absolute !important;
        left: 50% !important;
        top: 50% !important;
        max-width: none !important;
        max-height: none !important;
        transform-origin: center center !important;
        user-select: none !important;
        pointer-events: none !important;
        -webkit-user-drag: none;
        opacity: 0;
        transition: transform .12s ease, opacity .2s ease;
        will-change: transform;
      }

      .tm-artwork-image.is-ready {
        opacity: 1;
      }

      .tm-artwork-stage.is-dragging .tm-artwork-image,
      .tm-artwork-stage.is-pinching .tm-artwork-image {
        transition: none !important;
      }

      .tm-artwork-viewer .tm-artwork-close {
        position: absolute !important;
        top: 16px !important;
        right: 18px !important;
        z-index: 100 !important;
        display: flex !important;
        align-items: center;
        justify-content: center;
        width: 44px !important;
        height: 44px !important;
        padding: 0 !important;
        border: 1px solid rgba(255,255,255,.5) !important;
        border-radius: 50% !important;
        background: rgba(0,0,0,.7) !important;
        color: white !important;
        font: 28px Arial, sans-serif !important;
        cursor: pointer;
      }

      .tm-artwork-viewer .tm-artwork-arrow {
        position: absolute !important;
        top: 50% !important;
        z-index: 100 !important;
        display: flex !important;
        visibility: visible !important;
        align-items: center !important;
        justify-content: center !important;
        transform: translateY(-50%) !important;
        width: 48px !important;
        height: 58px !important;
        min-width: 48px !important;
        padding: 0 !important;
        margin: 0 !important;
        border: 1px solid rgba(255,255,255,.7) !important;
        border-radius: 8px !important;
        background: rgba(0,0,0,.85) !important;
        color: white !important;
        font: 36px Arial, sans-serif !important;
        line-height: 1 !important;
        box-shadow: 0 2px 12px rgba(0,0,0,.35);
        pointer-events: auto !important;
        cursor: pointer !important;
        -webkit-tap-highlight-color: transparent;
      }

      .tm-artwork-viewer .tm-artwork-prev {
        left: 10px !important;
        right: auto !important;
      }

      .tm-artwork-viewer .tm-artwork-next {
        right: 10px !important;
        left: auto !important;
      }

      .tm-artwork-viewer .tm-artwork-arrow:disabled {
        opacity: .35 !important;
        cursor: default !important;
      }

      .tm-artwork-viewer .tm-artwork-arrow:not(:disabled) {
        opacity: 1 !important;
      }

      .tm-artwork-viewer .tm-artwork-controls {
        position: absolute !important;
        bottom: 15px !important;
        left: 50% !important;
        transform: translateX(-50%) !important;
        z-index: 101 !important;
        display: flex !important;
        align-items: center;
        gap: 10px;
        padding: 8px 12px;
        border-radius: 30px;
        background: rgba(0,0,0,.75);
        white-space: nowrap;
      }

      .tm-artwork-viewer .tm-artwork-controls button {
        display: flex !important;
        align-items: center;
        justify-content: center;
        width: 38px !important;
        height: 38px !important;
        padding: 0 !important;
        border: 0 !important;
        border-radius: 50% !important;
        background: #fff !important;
        color: #111 !important;
        font: 22px Arial, sans-serif !important;
        cursor: pointer;
      }

      .tm-artwork-zoom-label {
        color: white !important;
        font: 13px Arial, sans-serif;
        min-width: 45px;
        text-align: center;
      }

      .tm-artwork-counter {
        position: absolute !important;
        top: 26px !important;
        left: 24px !important;
        z-index: 101 !important;
        color: white !important;
        font: 14px Arial, sans-serif;
      }

      body.tm-artwork-open {
        overflow: hidden !important;
      }

      @media screen and (max-width: 749px) {
        .tm-artwork-stage {
          inset: 65px 8px 80px !important;
        }

        .tm-artwork-viewer .tm-artwork-arrow {
          display: flex !important;
          visibility: visible !important;
          width: 44px !important;
          height: 52px !important;
          min-width: 44px !important;
          font-size: 32px !important;
          z-index: 100 !important;
        }

        .tm-artwork-viewer .tm-artwork-prev {
          left: 6px !important;
        }

        .tm-artwork-viewer .tm-artwork-next {
          right: 6px !important;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .tm-artwork-viewer,
        .tm-artwork-image {
          transition: none !important;
        }
      }
    `;

    document.head.appendChild(style);

    const viewer = document.createElement('div');
    viewer.className = 'tm-artwork-viewer';

    viewer.setAttribute('role', 'dialog');
    viewer.setAttribute('aria-modal', 'true');
    viewer.setAttribute('aria-label', 'Artwork viewer');

    viewer.innerHTML = `
      <span class="tm-artwork-counter"></span>

      <button
        type="button"
        class="tm-artwork-close"
        aria-label="Close viewer"
      >×</button>

      <div class="tm-artwork-stage">
        <img
          class="tm-artwork-image"
          alt=""
          draggable="false"
        >
      </div>

      <button
        type="button"
        class="tm-artwork-arrow tm-artwork-prev"
        aria-label="Previous image"
      >
        <svg
          width="24"
          height="32"
          viewBox="0 0 24 32"
          aria-hidden="true"
          style="display:block;pointer-events:none"
        >
          <path
            d="M18 3 L5 16 L18 29"
            fill="none"
            stroke="white"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <button
        type="button"
        class="tm-artwork-arrow tm-artwork-next"
        aria-label="Next image"
      >
        <svg
          width="24"
          height="32"
          viewBox="0 0 24 32"
          aria-hidden="true"
          style="display:block;pointer-events:none"
        >
          <path
            d="M6 3 L19 16 L6 29"
            fill="none"
            stroke="white"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>

      <div class="tm-artwork-controls">
        <button
          type="button"
          data-artwork-action="out"
          aria-label="Zoom out"
        >−</button>

        <span class="tm-artwork-zoom-label">100%</span>

        <button
          type="button"
          data-artwork-action="in"
          aria-label="Zoom in"
        >+</button>

        <button
          type="button"
          data-artwork-action="reset"
          aria-label="Reset zoom"
        >↺</button>
      </div>
    `;

    document.body.appendChild(viewer);

    this.viewer = viewer;
    this.viewerStage = viewer.querySelector('.tm-artwork-stage');
    this.viewerImage = viewer.querySelector('.tm-artwork-image');

    const stage = this.viewerStage;

    viewer.querySelector('.tm-artwork-close')
      .addEventListener('click', () => {
        this.closeArtworkViewer();
      });

    viewer.querySelector('.tm-artwork-prev')
      .addEventListener('click', event => {
        event.preventDefault();
        this.changeArtwork(-1);
      });

    viewer.querySelector('.tm-artwork-next')
      .addEventListener('click', event => {
        event.preventDefault();
        this.changeArtwork(1);
      });

    viewer.addEventListener('click', event => {
      if (event.target === viewer) {
        this.closeArtworkViewer();
      }
    });

    viewer.querySelectorAll('[data-artwork-action]')
      .forEach(button => {
        button.addEventListener('click', () => {
          const action = button.dataset.artworkAction;

          if (action === 'in') this.zoomArtwork(1.35);
          if (action === 'out') this.zoomArtwork(1 / 1.35);
          if (action === 'reset') this.resetViewerZoom();
        });
      });

    stage.addEventListener(
      'wheel',
      this.handleViewerWheel,
      { passive: false }
    );

    stage.addEventListener(
      'pointerdown',
      this.handleViewerPointerDown
    );

    stage.addEventListener(
      'pointermove',
      this.handleViewerPointerMove
    );

    stage.addEventListener(
      'pointerup',
      this.handleViewerPointerUp
    );

    stage.addEventListener(
      'pointercancel',
      this.handleViewerPointerUp
    );

    stage.addEventListener('dblclick', event => {
      event.preventDefault();

      const rect = stage.getBoundingClientRect();

      this.zoomArtwork(
        1.8,
        event.clientX - rect.left,
        event.clientY - rect.top
      );
    });

    stage.addEventListener(
      'touchstart',
      this.handleArtworkTouchStart,
      { passive: true }
    );

    stage.addEventListener(
      'touchmove',
      this.handleArtworkTouchMove,
      { passive: false }
    );

    stage.addEventListener(
      'touchend',
      this.handleArtworkTouchEnd,
      { passive: true }
    );

    stage.addEventListener(
      'touchcancel',
      this.handleArtworkTouchEnd,
      { passive: true }
    );

    window.addEventListener(
      'resize',
      this.handleViewerResize,
      { signal: this.#controller.signal }
    );
  }

  updateArtworkNavigation() {
    if (!this.viewer) return;

    const total = this.artworkImages.length;
    const index = this.artworkIndex;

    const previous = this.viewer.querySelector(
      '.tm-artwork-prev'
    );

    const next = this.viewer.querySelector(
      '.tm-artwork-next'
    );

    previous.disabled = index <= 0;
    next.disabled = index >= total - 1;

    // Keep navigation visible, even with one image.
    // This avoids theme CSS hiding the buttons.
    [previous, next].forEach(button => {
      button.removeAttribute('hidden');

      button.style.setProperty(
        'display', 'flex', 'important'
      );

      button.style.setProperty(
        'visibility', 'visible', 'important'
      );

      button.style.setProperty(
        'z-index', '100', 'important'
      );

      button.style.setProperty(
        'pointer-events', 'auto', 'important'
      );
    });

    previous.style.setProperty(
      'left', '6px', 'important'
    );

    next.style.setProperty(
      'right', '6px', 'important'
    );

    const counter = this.viewer.querySelector(
      '.tm-artwork-counter'
    );

    counter.textContent =
      total > 1 ? `${index + 1} / ${total}` : '';
  }

  loadArtwork(index) {
    if (
      index < 0 ||
      index >= this.artworkImages.length
    ) return;

    this.artworkIndex = index;
    this.updateArtworkNavigation();

    const original = this.artworkImages[index];
    const source = this.getArtworkSource(original);
    const image = this.viewerImage;
    const token = ++this.imageLoadToken;

    this.viewerState = {
      scale: 1,
      x: 0,
      y: 0,
      fit: 1,
      dragging: false,
      pointerId: null,
      lastX: 0,
      lastY: 0
    };

    this.touchMode = null;
    this.pinchDistance = 0;

    image.classList.remove('is-ready');
    image.alt = original.alt || 'Artwork';

    image.onload = () => {
      if (
        token !== this.imageLoadToken ||
        !this.viewerState
      ) return;

      this.fitArtwork();
      this.resetViewerZoom();

      requestAnimationFrame(() => {
        if (token === this.imageLoadToken) {
          image.classList.add('is-ready');
        }
      });
    };

    image.src = source;

    if (image.complete && image.naturalWidth) {
      this.fitArtwork();
      this.resetViewerZoom();
      image.classList.add('is-ready');
    }
  }

  changeArtwork(direction) {
    const nextIndex = this.artworkIndex + direction;

    if (
      nextIndex < 0 ||
      nextIndex >= this.artworkImages.length
    ) return;

    this.loadArtwork(nextIndex);
  }

  openArtworkViewer(image) {
    this.createArtworkViewer();

    this.artworkImages = this.collectArtworkImages();

    if (!this.artworkImages.includes(image)) {
      this.artworkImages.unshift(image);
    }

    this.previousFocus = document.activeElement;

    this.viewer.classList.add('is-open');
    document.body.classList.add('tm-artwork-open');

    this.loadArtwork(this.artworkImages.indexOf(image));

    this.viewer.querySelector('.tm-artwork-close').focus();
  }

  fitArtwork() {
    const state = this.viewerState;
    const image = this.viewerImage;
    const stage = this.viewerStage;

    if (
      !state ||
      !image.naturalWidth ||
      !image.naturalHeight
    ) return;

    state.fit = Math.min(
      stage.clientWidth / image.naturalWidth,
      stage.clientHeight / image.naturalHeight,
      1
    );

    this.applyViewerTransform();
  }

  applyViewerTransform() {
    const state = this.viewerState;
    const image = this.viewerImage;
    const stage = this.viewerStage;

    if (!state || !image?.naturalWidth) return;

    const w = image.naturalWidth * state.fit;
    const h = image.naturalHeight * state.fit;

    const maxX = Math.max(
      0,
      (w * state.scale - stage.clientWidth) / 2
    );

    const maxY = Math.max(
      0,
      (h * state.scale - stage.clientHeight) / 2
    );

    state.x = Math.max(-maxX, Math.min(maxX, state.x));
    state.y = Math.max(-maxY, Math.min(maxY, state.y));

    image.style.width = `${image.naturalWidth}px`;
    image.style.height = `${image.naturalHeight}px`;

    image.style.transform =
      `translate(calc(-50% + ${state.x}px), ` +
      `calc(-50% + ${state.y}px)) ` +
      `scale(${state.fit * state.scale})`;

    stage.classList.toggle(
      'is-zoomed',
      state.scale > 1
    );

    const label = this.viewer.querySelector(
      '.tm-artwork-zoom-label'
    );

    if (label) {
      label.textContent =
        `${Math.round(state.scale * 100)}%`;
    }
  }

  resetViewerZoom() {
    if (!this.viewerState) return;

    this.viewerState.scale = 1;
    this.viewerState.x = 0;
    this.viewerState.y = 0;

    this.applyViewerTransform();
  }

  zoomArtwork(factor, cx, cy) {
    const state = this.viewerState;
    const stage = this.viewerStage;

    if (
      !state ||
      !stage ||
      !Number.isFinite(factor)
    ) return;

    const centerX = cx ?? stage.clientWidth / 2;
    const centerY = cy ?? stage.clientHeight / 2;
    const oldScale = state.scale;

    state.scale = Math.max(
      1,
      Math.min(20, oldScale * factor)
    );

    const ratio = state.scale / oldScale;

    const offsetX = centerX - stage.clientWidth / 2;
    const offsetY = centerY - stage.clientHeight / 2;

    state.x =
      (state.x - offsetX) * ratio + offsetX;

    state.y =
      (state.y - offsetY) * ratio + offsetY;

    this.applyViewerTransform();
  }

  handleViewerWheel = (event) => {
    if (!this.viewerState) return;

    event.preventDefault();

    const rect = this.viewerStage.getBoundingClientRect();

    this.zoomArtwork(
      Math.exp(-event.deltaY * .0015),
      event.clientX - rect.left,
      event.clientY - rect.top
    );
  };

  handleViewerPointerDown = (event) => {
    if (event.pointerType === 'touch') return;

    const state = this.viewerState;

    if (
      !state ||
      state.scale <= 1 ||
      event.button !== 0
    ) return;

    event.preventDefault();

    state.dragging = true;
    state.pointerId = event.pointerId;
    state.lastX = event.clientX;
    state.lastY = event.clientY;

    this.viewerStage.setPointerCapture(
      event.pointerId
    );

    this.viewerStage.classList.add('is-dragging');
  };

  handleViewerPointerMove = (event) => {
    const state = this.viewerState;

    if (
      !state?.dragging ||
      state.pointerId !== event.pointerId
    ) return;

    state.x += event.clientX - state.lastX;
    state.y += event.clientY - state.lastY;

    state.lastX = event.clientX;
    state.lastY = event.clientY;

    this.applyViewerTransform();
  };

  handleViewerPointerUp = (event) => {
    const state = this.viewerState;

    if (
      !state ||
      state.pointerId !== event.pointerId
    ) return;

    state.dragging = false;
    state.pointerId = null;

    this.viewerStage.classList.remove('is-dragging');
  };

  handleArtworkTouchStart = (event) => {
    const state = this.viewerState;
    if (!state) return;

    if (event.touches.length === 2) {
      const a = event.touches[0];
      const b = event.touches[1];

      this.pinchDistance = Math.hypot(
        b.clientX - a.clientX,
        b.clientY - a.clientY
      );

      this.pinchScale = state.scale;
      this.touchMode = 'pinch';

      this.viewerStage.classList.add('is-pinching');
      return;
    }

    if (event.touches.length === 1) {
      const touch = event.touches[0];

      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;
      this.touchStartTime = Date.now();

      this.touchMode =
        state.scale > 1 ? 'pan' : 'swipe';
    }
  };

  handleArtworkTouchMove = (event) => {
    const state = this.viewerState;
    if (!state) return;

    if (
      event.touches.length === 2 &&
      this.pinchDistance > 0
    ) {
      event.preventDefault();

      const a = event.touches[0];
      const b = event.touches[1];

      const distance = Math.hypot(
        b.clientX - a.clientX,
        b.clientY - a.clientY
      );

      const targetScale = Math.max(
        1,
        Math.min(
          20,
          this.pinchScale * distance / this.pinchDistance
        )
      );

      const rect = this.viewerStage.getBoundingClientRect();

      const centerX =
        (a.clientX + b.clientX) / 2 - rect.left;

      const centerY =
        (a.clientY + b.clientY) / 2 - rect.top;

      this.zoomArtwork(
        targetScale / state.scale,
        centerX,
        centerY
      );

      return;
    }

    if (
      event.touches.length === 1 &&
      this.touchMode === 'pan' &&
      state.scale > 1
    ) {
      event.preventDefault();

      const touch = event.touches[0];

      state.x += touch.clientX - this.touchStartX;
      state.y += touch.clientY - this.touchStartY;

      this.touchStartX = touch.clientX;
      this.touchStartY = touch.clientY;

      this.applyViewerTransform();
    }
  };

  handleArtworkTouchEnd = (event) => {
    if (this.touchMode === 'pinch') {
      if (event.touches.length < 2) {
        this.pinchDistance = 0;
        this.touchMode = null;

        this.viewerStage.classList.remove(
          'is-pinching'
        );
      }

      return;
    }

    if (this.touchMode === 'pan') {
      if (event.touches.length === 0) {
        this.touchMode = null;
      }

      return;
    }

    if (
      this.touchMode !== 'swipe' ||
      event.touches.length > 0
    ) return;

    const touch = event.changedTouches[0];

    if (!touch) return;

    const dx = touch.clientX - this.touchStartX;
    const dy = touch.clientY - this.touchStartY;
    const elapsed = Date.now() - this.touchStartTime;

    this.touchMode = null;

    if (
      Math.abs(dx) > 55 &&
      Math.abs(dx) > Math.abs(dy) * 1.3 &&
      elapsed < 900
    ) {
      this.changeArtwork(dx < 0 ? 1 : -1);
    }
  };

  handleViewerKeyDown = (event) => {
    if (
      !this.viewer?.classList.contains('is-open')
    ) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeArtworkViewer();
    }

    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.changeArtwork(1);
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.changeArtwork(-1);
    }
  };

  handleViewerResize = () => {
    if (this.viewerState) {
      this.fitArtwork();
    }
  };

  closeArtworkViewer(immediate = false) {
    if (!this.viewer) return;

    ++this.imageLoadToken;

    this.viewer.classList.remove('is-open');
    document.body.classList.remove('tm-artwork-open');

    this.viewerState = null;
    this.touchMode = null;
    this.pinchDistance = 0;

    if (immediate) {
      this.viewer.remove();
      this.viewer = null;
      this.viewerStage = null;
      this.viewerImage = null;
    }

    if (
      !immediate &&
      this.previousFocus?.isConnected &&
      typeof this.previousFocus.focus === 'function'
    ) {
      this.previousFocus.focus();
    }
  }

  handleGalleryClick = (event) => {
    const image = this.getArtworkImage(event);

    if (!image) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    this.openArtworkViewer(image);
  };

  zoom(index, event) {
    event?.preventDefault?.();

    const container = this.refs.media?.[index];

    const image = container?.querySelector(
      'img.product-media__image'
    );

    if (image) {
      this.openArtworkViewer(image);
    }
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
